import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { func, bool, string, array } from 'prop-types';
import { useIntl } from '../../util/reactIntl';
import css from './SellerMessagesDrawer.module.css';

const SellerMessagesDrawer = props => {
  const { isOpen, onClose, listingTitle, messages, sendMessageInProgress, onSendMessage, onMarkRead } = props;
  const intl = useIntl();
  const [text, setText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (isOpen && onMarkRead) {
      onMarkRead();
    }
  }, [isOpen]);

  useEffect(() => {
    if (bottomRef.current && isOpen) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sendMessageInProgress) return;
    onSendMessage(trimmed).then(() => setText(''));
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = timestamp => {
    return intl.formatDate(new Date(timestamp), {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`${css.overlay} ${isOpen ? css.overlayVisible : ''}`}
        onClick={onClose}
      />
      <div className={`${css.drawer} ${isOpen ? css.drawerOpen : ''}`}>
        <div className={css.header}>
          <div className={css.headerTitle}>
            <span className={css.headerLabel}>Messages</span>
            {listingTitle ? <span className={css.headerListing}>{listingTitle}</span> : null}
          </div>
          <button className={css.closeButton} onClick={onClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        <div className={css.messageList}>
          {messages.length === 0 ? (
            <p className={css.emptyChat}>No messages yet.</p>
          ) : (
            messages.map((msg, index) => {
              const isSeller = msg.author === 'seller';
              return (
                <div key={index} className={isSeller ? css.messageSeller : css.messageAdmin}>
                  <div className={isSeller ? css.bubbleSeller : css.bubbleAdmin}>
                    {msg.subject ? <span className={css.subject}>{msg.subject}</span> : null}
                    <p className={css.bubbleText}>{msg.message}</p>
                    <span className={css.bubbleTime}>{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        <div className={css.inputArea}>
          <textarea
            className={css.textarea}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            placeholder="Type your message…"
            disabled={sendMessageInProgress}
          />
          <button
            className={css.sendButton}
            onClick={handleSend}
            disabled={!text.trim() || sendMessageInProgress}
          >
            Send
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

SellerMessagesDrawer.defaultProps = {
  messages: [],
  sendMessageInProgress: false,
  listingTitle: null,
  onMarkRead: null,
};

SellerMessagesDrawer.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  listingTitle: string,
  messages: array,
  sendMessageInProgress: bool,
  onSendMessage: func.isRequired,
  onMarkRead: func,
};

export default SellerMessagesDrawer;
